
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Clock, Linkedin, Twitter, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BlogPostContent from '@/components/BlogPostContent';
import EnhancedPageTransition from '@/components/EnhancedPageTransition';
import { article } from '@/data/blog/videomarketing-ecommerce-ki-produktvideos';
import { updateMetaTags, getCanonicalUrl, generateArticleSchema } from '@/utils/seoUtils';
import { getRelatedArticlesByTopic } from '@/data/blog';

const EnhancedEcommerceBlogPost = () => {
  const { lang } = useParams<{ lang: string }>();
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const [imageError, setImageError] = React.useState(false);
  
  // Related articles
  const relatedArticles = getRelatedArticlesByTopic('videomarketing-ecommerce-ki-produktvideos', 3);
  
  // Calculate reading time based on content (rough estimate)
  const contentLength = article.content[currentLanguage].length;
  const readingTimeMinutes = Math.max(Math.round(contentLength / 1000), 4); // Min 4 minutes
  
  // Fallback image if the main image fails to load
  const fallbackImage = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop";
  
  // Function to handle image loading errors
  const handleImageError = () => {
    console.error("Image failed to load:", article.image);
    setImageError(true);
  };

  useEffect(() => {
    const canonicalUrl = getCanonicalUrl(`blog/${article.slug}`, currentLanguage);
    const metaKeywords = "KI Videoproduktion, AI Video Marketing, E-Commerce Videos, Produktvideos KI, Online-Shop Videos, Conversion Optimierung, Future Level";
    
    updateMetaTags({
      title: `${article.meta[currentLanguage].title} | Future Level`,
      description: article.meta[currentLanguage].description,
      keywords: metaKeywords,
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogImage: article.image,
      ogType: "article"
    });
    
    generateArticleSchema(
      article.meta[currentLanguage].title,
      article.meta[currentLanguage].description,
      article.image,
      article.date,
      canonicalUrl,
      currentLanguage,
      metaKeywords
    );
  }, [currentLanguage]);

  // Animation variants
  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.7 } }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    hover: { y: -5, transition: { duration: 0.3 } }
  };

  return (
    <EnhancedPageTransition variant="fade" duration={0.4}>
      <div className="flex flex-col min-h-screen bg-[#121212]">
        <Header />
        <main className="flex-grow py-16 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]" itemScope itemType="https://schema.org/Article">
          <article className="container mx-auto px-4 sm:px-6">
            {/* Breadcrumbs */}
            <motion.nav 
              className="mb-6" 
              aria-label="Breadcrumb"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <ol className="flex items-center space-x-2 text-sm flex-wrap">
                <li>
                  <Link to={getLocalizedPath('/')} className="text-purple-400 hover:underline">
                    {currentLanguage === 'de' ? 'Startseite' : 
                     currentLanguage === 'en' ? 'Home' : 
                     currentLanguage === 'fr' ? 'Accueil' : 'Home'}
                  </Link>
                </li>
                <li>
                  <span className="text-gray-400 mx-2">/</span>
                  <Link to={getLocalizedPath('blog')} className="text-purple-400 hover:underline">
                    Blog
                  </Link>
                </li>
                <li>
                  <span className="text-gray-400 mx-2">/</span>
                  <span className="text-gray-300" itemProp="headline">{article.meta[currentLanguage].title}</span>
                </li>
              </ol>
            </motion.nav>
            
            <div className="max-w-4xl mx-auto">
              {/* Featured Image */}
              <motion.div 
                className="w-full h-72 md:h-96 rounded-xl mb-8 shadow-lg overflow-hidden"
                variants={contentVariants}
                initial="initial"
                animate="animate"
              >
                <img 
                  src={imageError ? fallbackImage : article.image || fallbackImage}
                  alt={article.meta[currentLanguage].title}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  itemProp="image"
                  onError={handleImageError}
                  loading="eager"
                  width="800"
                  height="450"
                />
              </motion.div>
              
              {/* Content Card */}
              <motion.div 
                className="glass-card rounded-xl p-6 md:p-8 shadow-lg mb-6"
                variants={contentVariants}
                initial="initial"
                animate="animate"
                transition={{ delay: 0.2 }}
              >
                {/* Reading Time & Date - Added Clock icon */}
                <div className="flex items-center justify-between mb-8">
                  <time dateTime={article.date} className="text-sm text-purple-300" itemProp="datePublished">
                    {article.date}
                  </time>
                  <div className="flex items-center text-sm text-purple-300">
                    <Clock size={16} className="mr-1.5" />
                    <span>{readingTimeMinutes} {currentLanguage === 'de' ? 'Min. Lesezeit' : 
                                               currentLanguage === 'en' ? 'min read' : 
                                               currentLanguage === 'fr' ? 'min de lecture' : 
                                               'min di lettura'}</span>
                  </div>
                </div>
                
                {/* Article Title - H1 for main title */}
                <motion.h1 
                  className="text-3xl md:text-4xl font-bold mt-2 mb-8 text-white font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-400" 
                  itemProp="headline"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  {article.meta[currentLanguage].title}
                </motion.h1>
                
                {/* Article Description - Using text-lg and text-gray-200 */}
                <motion.p
                  className="text-lg mb-8 text-gray-200"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  {article.meta[currentLanguage].description}
                </motion.p>
                
                {/* Social Sharing Options */}
                <div className="flex space-x-3 mb-12">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-purple-300 border-purple-700/30 hover:bg-purple-700/20"
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin size={16} className="mr-1" />
                    <span className="hidden sm:inline">LinkedIn</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-purple-300 border-purple-700/30 hover:bg-purple-700/20"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.meta[currentLanguage].title)}`, '_blank')}
                    aria-label="Share on Twitter"
                  >
                    <Twitter size={16} className="mr-1" />
                    <span className="hidden sm:inline">Twitter</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-purple-300 border-purple-700/30 hover:bg-purple-700/20"
                    onClick={() => window.open(`mailto:?subject=${encodeURIComponent(article.meta[currentLanguage].title)}&body=${encodeURIComponent(window.location.href)}`, '_blank')}
                    aria-label="Share via Email"
                  >
                    <Mail size={16} className="mr-1" />
                    <span className="hidden sm:inline">Email</span>
                  </Button>
                </div>
                
                {/* Article Content - Using custom styling for content */}
                <div className="prose prose-invert prose-lg max-w-none text-gray-200 mb-12">
                  {/* Custom rendering instead of using BlogPostContent to ensure proper formatting */}
                  <div 
                    className="article-content text-gray-200" 
                    dangerouslySetInnerHTML={{ __html: article.content[currentLanguage] }} 
                  />
                </div>
                
                {/* Pull Quote - With purple styling and border-l-4 */}
                <blockquote className="border-l-4 border-purple-500 pl-6 my-12 py-4 bg-purple-900/20 rounded-r-lg">
                  <p className="text-xl italic text-gray-100">
                    {currentLanguage === 'de' ? 
                      "Ein gut gemachtes Video verkauft nicht nur ein Produkt – es erzählt eine Geschichte, baut Vertrauen auf und transportiert Emotionen." : 
                     currentLanguage === 'en' ? 
                      "A well-made video doesn't just sell a product – it tells a story, builds trust, and conveys emotions." : 
                     currentLanguage === 'fr' ? 
                      "Une vidéo bien faite ne vend pas seulement un produit - elle raconte une histoire, instaure la confiance et transmet des émotions." : 
                      "Un video ben fatto non si limita a vendere un prodotto - racconta una storia, costruisce fiducia e trasmette emozioni."}
                  </p>
                </blockquote>
                
                {/* Article Tags - With rounded pill styling */}
                <div className="my-12">
                  <h3 className="text-sm uppercase text-gray-400 mb-4 tracking-wider">
                    {currentLanguage === 'de' ? 'Themen' : 
                     currentLanguage === 'en' ? 'Topics' : 
                     currentLanguage === 'fr' ? 'Sujets' : 
                     'Argomenti'}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <span className="px-4 py-1.5 bg-purple-900/30 text-purple-200 text-sm rounded-full border border-purple-500/20">
                      E-Commerce
                    </span>
                    <span className="px-4 py-1.5 bg-purple-900/30 text-purple-200 text-sm rounded-full border border-purple-500/20">
                      Videomarketing
                    </span>
                    <span className="px-4 py-1.5 bg-purple-900/30 text-purple-200 text-sm rounded-full border border-purple-500/20">
                      KI
                    </span>
                    <span className="px-4 py-1.5 bg-purple-900/30 text-purple-200 text-sm rounded-full border border-purple-500/20">
                      Produktvideos
                    </span>
                  </div>
                </div>
                
                {/* Single CTA Box - With gradient background and no camera emoji */}
                <div className="bg-gradient-to-r from-purple-900/50 to-purple-800/40 p-8 my-12 rounded-xl border border-purple-500/30 shadow-lg">
                  <h3 className="text-2xl font-bold mb-4 text-white">
                    {currentLanguage === 'de' ? "Bereit, Ihr E-Commerce auf das nächste Level zu heben?" : 
                     currentLanguage === 'en' ? "Ready to take your e-commerce to the next level?" : 
                     currentLanguage === 'fr' ? "Prêt à faire passer votre e-commerce au niveau supérieur?" : 
                     "Pronto a portare il tuo e-commerce al livello successivo?"}
                  </h3>
                  <p className="mb-6 text-lg text-gray-200">
                    {currentLanguage === 'de' ? "Kontaktieren Sie Future Level und erfahren Sie, wie KI Ihr Videomarketing transformiert." : 
                     currentLanguage === 'en' ? "Contact Future Level and discover how AI transforms your video marketing." : 
                     currentLanguage === 'fr' ? "Contactez Future Level et découvrez comment l'IA transforme votre marketing vidéo." : 
                     "Contatta Future Level e scopri come l'IA trasforma il tuo video marketing."}
                  </p>
                  <Link 
                    to={getLocalizedPath('contact')} 
                    className="inline-block px-8 py-3.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    {currentLanguage === 'de' ? "Jetzt unverbindlich beraten lassen" : 
                     currentLanguage === 'en' ? "Get a free consultation now" : 
                     currentLanguage === 'fr' ? "Obtenir une consultation gratuite" : 
                     "Ottieni una consulenza gratuita"}
                  </Link>
                </div>
                
                {/* Author Bio Section - With image and description */}
                <div className="mt-12 pt-8 border-t border-purple-900/30">
                  <div className="flex items-center">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-purple-500/30">
                      <img 
                        src="https://images.unsplash.com/photo-1568602471122-7832951cc4c5?q=80&w=150&auto=format&fit=crop" 
                        alt="Future Level Team" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="ml-5">
                      <h3 className="text-xl text-white font-semibold mb-1">Future Level Team</h3>
                      <p className="text-gray-200">
                        {currentLanguage === 'de' ? "Experten für KI-gestütztes Videomarketing in der Schweiz" : 
                         currentLanguage === 'en' ? "AI-powered video marketing experts in Switzerland" : 
                         currentLanguage === 'fr' ? "Experts en marketing vidéo basé sur l'IA en Suisse" : 
                         "Esperti di video marketing basato sull'IA in Svizzera"}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        {currentLanguage === 'de' ? "Die erste KI-Videoagentur der Schweiz" : 
                         currentLanguage === 'en' ? "Switzerland's first AI video agency" : 
                         currentLanguage === 'fr' ? "La première agence vidéo IA de Suisse" : 
                         "La prima agenzia video IA della Svizzera"}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Related Articles Section - With hover effects */}
                {relatedArticles.length > 0 && (
                  <motion.div 
                    className="mt-12 border-t border-purple-900/30 pt-12"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                  >
                    <h3 className="text-2xl font-semibold text-white mb-8 font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-400">
                      {currentLanguage === 'de' ? "Verwandte Artikel" :
                       currentLanguage === 'en' ? "Related Articles" :
                       currentLanguage === 'fr' ? "Articles Connexes" :
                       "Articoli Correlati"}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {relatedArticles.map((article, idx) => (
                        <motion.div 
                          key={idx}
                          variants={cardVariants}
                          initial="initial"
                          animate="animate"
                          whileHover="hover"
                          transition={{ delay: 0.8 + (idx * 0.1) }}
                        >
                          <Link 
                            to={`/${currentLanguage}/blog/${article.slug}`}
                            className="block h-full"
                          >
                            <div className="rounded-lg overflow-hidden bg-[#1A1F2C] hover:bg-[#222a3a] transition-colors border border-purple-900/20 h-full shadow-md hover:shadow-lg hover:shadow-purple-500/10">
                              <div className="relative overflow-hidden h-40">
                                <motion.img 
                                  src={article.image || fallbackImage} 
                                  alt={article.meta[currentLanguage].title}
                                  className="w-full h-full object-cover"
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ duration: 0.4 }}
                                  loading="lazy"
                                  width="300"
                                  height="160"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                              </div>
                              <div className="p-5">
                                <p className="text-xs text-purple-400 mb-2">{article.date}</p>
                                <h4 className="text-white font-semibold mb-3 transition-colors">
                                  {article.meta[currentLanguage].title}
                                </h4>
                                <p className="text-gray-300 text-sm line-clamp-2 mb-4">
                                  {article.meta[currentLanguage].description}
                                </p>
                                <div className="flex items-center mt-3 text-sm text-purple-300">
                                  {currentLanguage === 'de' ? "Weiterlesen" :
                                   currentLanguage === 'en' ? "Read more" :
                                   currentLanguage === 'fr' ? "Lire plus" :
                                   "Leggi di più"}
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </EnhancedPageTransition>
  );
};

export default EnhancedEcommerceBlogPost;
